import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Smartphone, Save, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

export function PaymentFeesConfig() {
  const [stripeFee, setStripeFee] = useState('1.6');
  const [maishapayCardFee, setMaishapayCardFee] = useState('2');
  const [dusupayCardFee, setDusupayCardFee] = useState('2');
  const [mobileFee, setMobileFee] = useState('3.5');
  const [withdrawalFee, setWithdrawalFee] = useState('2.5');
  const [maishapayB2cFee, setMaishapayB2cFee] = useState('3.5');
  const [cardProvider, setCardProvider] = useState<'stripe' | 'dusupay' | 'maishapay'>('stripe');
  const [mobileProvider, setMobileProvider] = useState<'flutterwave' | 'dusupay' | 'maishapay'>('flutterwave');
  const [savingProvider, setSavingProvider] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const readNumericSetting = (raw: unknown, fallback: number) => {
    if (raw === null || raw === undefined) return fallback;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsedJson = JSON.parse(raw);
        return readNumericSetting(parsedJson, fallback);
      } catch {
        // Fall back to direct numeric parsing for legacy plain-text values.
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in (raw as Record<string, unknown>)) {
      const nested = (raw as Record<string, unknown>).value;
      if (typeof nested === 'number' && Number.isFinite(nested)) return nested;
      if (typeof nested === 'string') {
        const parsed = Number(nested);
        return Number.isFinite(parsed) ? parsed : fallback;
      }
    }
    return fallback;
  };

  const parseFeeInput = (raw: string, fallback: number) => {
    const normalized = raw.replace(',', '.').trim();
    if (!normalized) return fallback;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  useEffect(() => {
    loadFees();
  }, []);

  const loadFees = async () => {
    try {
      const { data: stripeData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'stripe_fee_percentage')
        .maybeSingle();

      const { data: mobileData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'mobile_money_fee_percentage')
        .maybeSingle();

      const { data: maishaCardData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maishapay_card_fee_percentage')
        .maybeSingle();

      const { data: dusupayCardData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'dusupay_card_fee_percentage')
        .maybeSingle();

      const { data: withdrawalData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'withdrawal_fee_percentage')
        .maybeSingle();

      const { data: maishapayB2cData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maishapay_b2c_fee_percentage')
        .maybeSingle();

      // Read fees — handle both plain number and {value: X} wrapped formats
      setStripeFee(String(readNumericSetting(stripeData?.value, 1.6)));
      setMobileFee(String(readNumericSetting(mobileData?.value, 2.0)));
      setMaishapayCardFee(String(readNumericSetting(maishaCardData?.value, 2.0)));
      setDusupayCardFee(String(readNumericSetting(dusupayCardData?.value, 2.0)));
      setWithdrawalFee(String(readNumericSetting(withdrawalData?.value, 2.5)));
      setMaishapayB2cFee(String(readNumericSetting(maishapayB2cData?.value, 3.5)));

      // Load mobile money provider setting
      const { data: providerData } = await (supabase as any)
        .from('app_settings')
        .select('value')
        .eq('key', 'mobile_money_provider')
        .maybeSingle();

      if (providerData?.value === 'dusupay' || providerData?.value === 'flutterwave' || providerData?.value === 'maishapay') {
        setMobileProvider(providerData.value);
      }

      const { data: cardProviderData } = await (supabase as any)
        .from('app_settings')
        .select('value')
        .eq('key', 'card_payment_provider')
        .maybeSingle();

      if (cardProviderData?.value === 'stripe' || cardProviderData?.value === 'dusupay' || cardProviderData?.value === 'maishapay') {
        setCardProvider(cardProviderData.value);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des frais:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveFees = async () => {
    setSaving(true);
    try {
      const stripeFeeValue = parseFeeInput(stripeFee, 1.6);
      const mobileFeeValue = parseFeeInput(mobileFee, 2.0);
      const maishapayCardFeeValue = parseFeeInput(maishapayCardFee, 2.0);
      const dusupayCardFeeValue = parseFeeInput(dusupayCardFee, 2.0);

      // Store as { value: X } JSON object — consistent with how PaymentMethod.tsx reads it
      const withdrawalFeeValue = parseFeeInput(withdrawalFee, 2.5);
      const maishapayB2cFeeValue = parseFeeInput(maishapayB2cFee, 3.5);

      const payload = [
        { key: 'stripe_fee_percentage', value: { value: stripeFeeValue }, category: 'payment', data_type: 'number' },
        { key: 'mobile_money_fee_percentage', value: { value: mobileFeeValue }, category: 'payment', data_type: 'number' },
        { key: 'maishapay_card_fee_percentage', value: { value: maishapayCardFeeValue }, category: 'payment', data_type: 'number' },
        { key: 'dusupay_card_fee_percentage', value: { value: dusupayCardFeeValue }, category: 'payment', data_type: 'number' },
        { key: 'withdrawal_fee_percentage', value: { value: withdrawalFeeValue }, category: 'payment', data_type: 'number' },
        { key: 'maishapay_b2c_fee_percentage', value: { value: maishapayB2cFeeValue }, category: 'payment', data_type: 'number' },
      ].map((row) => ({ ...row, updated_at: new Date().toISOString() }));

      for (const row of payload) {
        const { error: upsertError } = await supabase
          .from('site_settings')
          .upsert(row, { onConflict: 'key' });
        if (upsertError) {
          throw new Error(`Erreur sauvegarde ${row.key}: ${upsertError.message}`);
        }
      }

      setStripeFee(String(stripeFeeValue));
      setMobileFee(String(mobileFeeValue));
      setMaishapayCardFee(String(maishapayCardFeeValue));
      setDusupayCardFee(String(dusupayCardFeeValue));
      setWithdrawalFee(String(withdrawalFeeValue));
      setMaishapayB2cFee(String(maishapayB2cFeeValue));

      toast({
        title: t('admin.payment_fees.updated'),
        description: t('admin.payment_fees.updated_desc')
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('admin.payment_fees.error'),
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveMobileProvider = async (provider: 'flutterwave' | 'dusupay' | 'maishapay') => {
    setSavingProvider(true);
    try {
      const { error } = await (supabase as any)
        .from('app_settings')
        .upsert(
          { key: 'mobile_money_provider', value: provider, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      if (error) throw error;
      setMobileProvider(provider);
      const providerLabel = provider === 'dusupay' ? 'Dusupay' : provider === 'maishapay' ? 'MaishaPay' : 'Flutterwave';
      toast({
        title: "Fournisseur mis à jour",
        description: `Mobile money : ${providerLabel} activé.`,
      });
    } catch (err) {
      toast({ title: "Erreur", description: "Impossible de changer le fournisseur.", variant: "destructive" });
    } finally {
      setSavingProvider(false);
    }
  };

  const saveCardProvider = async (provider: 'stripe' | 'dusupay' | 'maishapay') => {
    setSavingProvider(true);
    try {
      const { error } = await (supabase as any)
        .from('app_settings')
        .upsert(
          { key: 'card_payment_provider', value: provider, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );

      if (error) throw error;
      setCardProvider(provider);
      const providerLabel = provider === 'dusupay' ? 'Dusupay' : provider === 'maishapay' ? 'MaishaPay' : 'Stripe';
      toast({
        title: "Fournisseur mis à jour",
        description: `Paiement carte : ${providerLabel} activé.`,
      });
    } catch (err) {
      toast({ title: "Erreur", description: "Impossible de changer le fournisseur carte.", variant: "destructive" });
    } finally {
      setSavingProvider(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">{t('admin.payment_fees.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('admin.payment_fees.title')}
        </CardTitle>
        <CardDescription>
          {t('admin.payment_fees.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stripe-fee" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              {t('admin.payment_fees.stripe')}
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="stripe-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={stripeFee}
                onChange={(e) => setStripeFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                {t('admin.payment_fees.example')} : {stripeFee || '0'}% {t('admin.payment_fees.on')} 100 000 CDF = {Math.round(100000 * (parseFeeInput(stripeFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('admin.payment_fees.stripe_desc')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile-fee" className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              {t('admin.payment_fees.mobile')}
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="mobile-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={mobileFee}
                onChange={(e) => setMobileFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                {t('admin.payment_fees.example')} : {mobileFee || '0'}% {t('admin.payment_fees.on')} 100 000 CDF = {Math.round(100000 * (parseFeeInput(mobileFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('admin.payment_fees.mobile_desc')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maishapay-card-fee" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Frais carte MaishaPay (%)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="maishapay-card-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={maishapayCardFee}
                onChange={(e) => setMaishapayCardFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                Exemple : {maishapayCardFee || '0'}% sur 100 000 CDF = {Math.round(100000 * (parseFeeInput(maishapayCardFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dusupay-card-fee" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Frais carte Dusupay (%)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="dusupay-card-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={dusupayCardFee}
                onChange={(e) => setDusupayCardFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                Exemple : {dusupayCardFee || '0'}% sur 100 000 CDF = {Math.round(100000 * (parseFeeInput(dusupayCardFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="withdrawal-fee" className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Frais de retrait plateforme (%)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="withdrawal-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={withdrawalFee}
                onChange={(e) => setWithdrawalFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                Exemple : {withdrawalFee || '0'}% sur 100 000 CDF = {Math.round(100000 * (parseFeeInput(withdrawalFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Frais déduits du montant de retrait pour tous les modes (mobile money et virement).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maishapay-b2c-fee" className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Frais MaishaPay B2C / Mobile Money retrait (%)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="maishapay-b2c-fee"
                type="text"
                inputMode="decimal"
                pattern="[0-9]+([.,][0-9]+)?"
                value={maishapayB2cFee}
                onChange={(e) => setMaishapayB2cFee(e.target.value)}
                className="max-w-[200px]"
              />
              <span className="text-sm text-muted-foreground">
                Exemple : {maishapayB2cFee || '0'}% sur 100 000 CDF = {Math.round(100000 * (parseFeeInput(maishapayB2cFee, 0) / 100)).toLocaleString()} CDF
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Frais opérateur MaishaPay appliqués uniquement sur les retraits par Mobile Money.
            </p>
          </div>
        </div>

        <Button onClick={saveFees} disabled={saving} className="w-full sm:w-auto">
          <Save className="mr-2 h-4 w-4" />
          {saving ? t('admin.payment_fees.saving') : t('admin.payment_fees.save')}
        </Button>

        {/* Mobile money provider switch */}
        <div className="border-t pt-6 space-y-3">
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium">
              <CreditCard className="h-4 w-4" />
              Fournisseur carte actif
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Change le processeur utilisé pour tous les paiements par carte.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant={cardProvider === 'stripe' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveCardProvider('stripe')}
            >
              {cardProvider === 'stripe' && '✓ '}Stripe
            </Button>
            <Button
              variant={cardProvider === 'dusupay' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveCardProvider('dusupay')}
            >
              {cardProvider === 'dusupay' && '✓ '}Dusupay
            </Button>
            <Button
              variant={cardProvider === 'maishapay' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveCardProvider('maishapay')}
            >
              {cardProvider === 'maishapay' && '✓ '}MaishaPay
            </Button>
          </div>

          <Separator />

          <div>
            <Label className="flex items-center gap-2 text-sm font-medium">
              <RefreshCw className="h-4 w-4" />
              Fournisseur Mobile Money actif
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Change le processeur utilisé pour tous les paiements mobile money (MTN, Airtel, etc.)
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant={mobileProvider === 'flutterwave' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveMobileProvider('flutterwave')}
            >
              {mobileProvider === 'flutterwave' && '✓ '}Flutterwave
            </Button>
            <Button
              variant={mobileProvider === 'dusupay' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveMobileProvider('dusupay')}
            >
              {mobileProvider === 'dusupay' && '✓ '}Dusupay
            </Button>
            <Button
              variant={mobileProvider === 'maishapay' ? 'default' : 'outline'}
              size="sm"
              disabled={savingProvider}
              onClick={() => saveMobileProvider('maishapay')}
            >
              {mobileProvider === 'maishapay' && '✓ '}MaishaPay
            </Button>
          </div>
          {mobileProvider === 'dusupay' && (
            <p className="text-xs text-amber-600">
              ⚠️ Dusupay nécessite DUSUPAY_PUBLIC_KEY et DUSUPAY_SECRET_KEY dans les variables d'environnement Supabase.
            </p>
          )}
          {mobileProvider === 'maishapay' && (
            <p className="text-xs text-amber-600">
              ⚠️ MaishaPay nécessite MAISHAPAY_PUBLIC_KEY et MAISHAPAY_SECRET_KEY. Les endpoints officiels sont utilisés par défaut (MAISHAPAY_BASE_URL/ENDPOINTS en option).
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
